import { useEffect, useState, type FunctionComponent } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import size from 'lodash/size';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { useUniversalNavigationContext } from '@fff-web/fff-global-nav';
import { showErrorToast, showSuccessToast, Toast } from '@fff-web/fff-ui-shared';
import Layout from '../../components/Layout';
import useScreenMode from '../../hooks/useScreenMode';
import viewSizeTypes from '../../utils/viewSizeTypes';
import { SUBSCRIPTION_STATUS } from '../constants';
import MyAccountNav from './MyAccountNav/MyAccountNav';
import Profile from './Profile/Profile';
import MyAccountRoutes from './routes/MyAccountRoutes';
import { fetchUserAvatarRequest, fetchUserAvatarSuccess } from './services/actions/userActions';
import {
  getRemainingBoxes,
  getUserCredits,
  getUserNextRenewal,
  setUserAvatar,
} from './services/api/account';
import { getBillingInfo } from './services/api/billing';
import { VIEW_SIZE } from './utils/device';

const ANNUAL_SUBSCRIPTION = 'annual';
const CLUB_MEMBER = 'club';

export type SubscriptionStatus = keyof typeof SUBSCRIPTION_STATUS;

type MyAccountPageProps = {
  firstName: string;
  lastName: string;
  activationDate: string;
  shippingAddress: Record<string, unknown>;
  subscriptionPlan: string;
  avatarUrl: string;
  updateAvatar: (userAvatarUrl: string) => void;
  phone: string;
  email: string;
  getUserAvatar: () => void;
  updateAccount: () => void;
  fetchingUserStatus: boolean;
  userStatusError: boolean;
  userRole: string;
  planCode: string;
};

const MyAccountPage: FunctionComponent<MyAccountPageProps> = ({
  firstName,
  lastName,
  activationDate,
  shippingAddress,
  subscriptionPlan,
  avatarUrl,
  updateAvatar,
  phone,
  email,
  getUserAvatar,
  fetchingUserStatus,
  userStatusError,
  userRole,
  planCode,
}) => {
  const {
    membershipInfo,
    userSubscriptions: {
      clubMembershipSubscription,
      boxSubscription,
      refetch: refetchSubscriptions,
    },
  } = useUniversalNavigationContext();
  const isClubMember = userRole === CLUB_MEMBER;
  const membershipState = membershipInfo?.membership_state;
  const rawStatus = isClubMember
    ? (clubMembershipSubscription?.status ?? membershipState)
    : membershipState;
  const subscriptionState = rawStatus?.toUpperCase() as SubscriptionStatus;

  const { myAccountRemainingBoxes: myAccountRemainingBoxesFF, chatbotAppliedLab: showChatbot } =
    useFlags();
  const screenMode = useScreenMode();
  const [credits, setCredits] = useState<
    {
      amount: number;
      creditName: string;
    }[]
  >([]);
  const [billingInfo, setBillingInfo] = useState({});
  const [remainingBoxes, setRemainingBoxes] = useState<
    {
      orderState: string;
      season: string;
    }[]
  >([]);
  const [nextRenewal, setNextRenewal] = useState<{
    year: number;
    season: string;
    renewalDate: string | Date;
  }>({ year: 0, season: '', renewalDate: '' });
  const viewSize = viewSizeTypes[screenMode as keyof typeof viewSizeTypes];
  const isDesktop = viewSize === VIEW_SIZE.large || viewSize === VIEW_SIZE.xLarge;
  const displayRemainingBoxes =
    subscriptionPlan === ANNUAL_SUBSCRIPTION && myAccountRemainingBoxesFF && !!size(remainingBoxes);

  const nextRenewalInfo = isClubMember
    ? {
        renewalDate:
          subscriptionState === 'CANCELED' ? '' : (clubMembershipSubscription?.nextBillDate ?? ''),
        season: '',
        year: 0,
      }
    : nextRenewal;

  useEffect(() => {
    const fetchCredits = async () => {
      const fetchedCredits = await getUserCredits();
      setCredits(fetchedCredits);
    };
    const fetchBillingInfo = async () => {
      const response = await getBillingInfo();
      setBillingInfo({ ...response, postalCode: response.zip });
    };
    const fetchUserNextRenewal = async () => {
      const response = await getUserNextRenewal();
      if (size(response)) setNextRenewal(response);
    };
    fetchCredits();
    fetchBillingInfo();
    if (!avatarUrl) getUserAvatar();
    if (subscriptionState !== SUBSCRIPTION_STATUS.EXPIRED && !isClubMember) fetchUserNextRenewal();
  }, [isClubMember]);

  useEffect(() => {
    const fetchRemainingBoxes = async () => {
      const items = await getRemainingBoxes();
      if (Array.isArray(items)) {
        setRemainingBoxes(items);
      }
    };
    if (subscriptionPlan === ANNUAL_SUBSCRIPTION) {
      fetchRemainingBoxes();
    }
  }, [subscriptionPlan]);

  const uploadMyAvatar = async (formData: FormData) => {
    try {
      const { userAvatarUrl } = await setUserAvatar(formData);
      updateAvatar(userAvatarUrl);
      showSuccessToast(
        'Updated Profile Photo',
        'Your profile photo change has been successfully saved.',
      );
    } catch (_) {
      showErrorToast(
        'Profile Picture Upload Failed',
        'Your image did not meet our requirements. Please upload a PNG or JPG of at least 300x300 that is under 500KB.',
      );
    }
  };

  const boxSubscriptionId = boxSubscription?.status !== 'CANCELED' ? boxSubscription?.id : null;

  return (
    <Layout isLoading={userStatusError || fetchingUserStatus} hideChatWidget={!showChatbot}>
      <div className={`pandora ${viewSize}`} data-testid="general-container">
        <Profile
          isDesktop={isDesktop}
          firstName={firstName}
          lastName={lastName}
          activationDate={
            isClubMember && clubMembershipSubscription?.startAt
              ? clubMembershipSubscription.startAt
              : activationDate
          }
          subscriptionPlan={subscriptionPlan}
          subscriptionState={subscriptionState}
          avatarUrl={avatarUrl}
          uploadAvatar={uploadMyAvatar}
          remainingBoxes={remainingBoxes}
          displayRemainingBoxes={displayRemainingBoxes}
          nextRenewal={nextRenewalInfo}
          membershipInfo={membershipInfo}
          planCode={planCode}
          isClubMember={isClubMember}
        />
        {isDesktop && <MyAccountNav />}
        <MyAccountRoutes
          firstName={firstName}
          lastName={lastName}
          shippingAddress={shippingAddress}
          phone={phone}
          email={email}
          credits={credits}
          billingInfo={billingInfo}
          setBillingInfo={setBillingInfo}
          isDesktop={isDesktop}
          isClubMember={isClubMember}
          clubMemberSubscriptionId={clubMembershipSubscription?.id}
          subscriptionState={subscriptionState}
          refetchClubMemberSubscription={refetchSubscriptions}
          boxSubscriptionId={boxSubscriptionId}
        />
        <Toast />
      </div>
    </Layout>
  );
};

const mapStateToProps = (state) => {
  const { userState, viewState } = state;
  return {
    viewSize: viewState.viewSize,
    firstName: userState.firstName,
    lastName: userState.lastName,
    activationDate: userState.activationDate,
    shippingAddress: userState.shippingAddress,
    subscriptionPlan: userState.subscription,
    userId: userState.userId,
    userRole: userState.role,
    avatarUrl: userState.userAvatarUrl,
    uploadingImageError: userState.error,
    phone: userState.phoneNumber,
    email: userState.email,
    fetchingUserStatus: userState.fetchingUserStatus,
    userStatusError: userState.userStatusError,
    planCode: userState.planCode,
  };
};

export default compose(
  connect(mapStateToProps, {
    getUserAvatar: fetchUserAvatarRequest,
    updateAvatar: fetchUserAvatarSuccess,
  }),
)(MyAccountPage);
